import { useState, useEffect, useCallback } from 'react';
import LocalSearchSelect from '../components/LocalSearchSelect';
import { ArrowRightLeft, Truck, PackageCheck } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import api from '../api/client';

interface Branch { id: number; name: string; }
interface Location { id: number; name: string; branch_id: number; }
interface InventoryItem { product_id: number; product_name: string; sku: string; qty_available: number; cost_price: number; }

function statusOf(t: any): 'draft' | 'in_transit' | 'received' {
  if (t.received_at) return 'received';
  if (t.dispatched_at) return 'in_transit';
  return 'draft';
}

function StatusBadge({ status }: { status: string }) {
  const map: any = {
    draft: { bg: 'var(--sur2)', color: 'var(--tx3)', label: 'Draft' },
    in_transit: { bg: 'rgba(234,179,8,0.15)', color: '#eab308', label: 'In Transit' },
    received: { bg: 'rgba(34,197,94,0.15)', color: 'var(--green)', label: 'Received' },
  };
  const s = map[status] || map.draft;
  return <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{s.label}</span>;
}

export default function StockTransfer() {
  const { showToast } = useToast();
  const [branches, setBranches]   = useState<Branch[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [coa, setCoa]             = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [fromStock, setFromStock] = useState<InventoryItem[]>([]);

  const emptyForm = { from_branch_id:'', to_branch_id:'', from_location_id:'', to_location_id:'', transfer_date: new Date().toISOString().split('T')[0], notes:'' };
  const [form, setForm]           = useState<any>(emptyForm);
  const [lines, setLines]         = useState<any[]>([{ product_id:'', qty:'' }]);

  // Receive modal state
  const [receiveTarget, setReceiveTarget] = useState<any>(null);
  const [receiveDate, setReceiveDate]     = useState(new Date().toISOString().split('T')[0]);
  const [receiveLanded, setReceiveLanded] = useState<any[]>([]);
  const [receiving, setReceiving]         = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/stock-transfers').then(r => { setTransfers(r.data.data); setLoading(false); });
  }, []);

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data.data));
    api.get('/locations').then(r => setLocations(r.data.data));
    api.get('/coa').then(r => setCoa(r.data.data));
    load();
  }, [load]);

  useEffect(() => {
    if (form.from_location_id) {
      api.get('/inventory', { params: { location_id: form.from_location_id, limit: 200 } })
        .then(r => setFromStock(r.data.data || []));
    } else { setFromStock([]); }
  }, [form.from_location_id]);

  const fromLocations = locations.filter(l => !form.from_branch_id || String(l.branch_id) === String(form.from_branch_id));
  const toLocations   = locations.filter(l => !form.to_branch_id   || String(l.branch_id) === String(form.to_branch_id));

  const addLine    = () => setLines(l => [...l, { product_id:'', qty:'' }]);
  const removeLine = (i: number) => setLines(l => l.filter((_,idx) => idx !== i));
  const updateLine = (i: number, f: string, v: string) => setLines(l => l.map((line,idx) => idx===i ? {...line,[f]:v} : line));

  const handleSubmit = async () => {
    if (!form.from_branch_id || !form.to_branch_id || !form.from_location_id || !form.to_location_id) { showToast('Please fill all branch and location fields', 'error'); return; }
    const items = lines.filter(l => l.product_id && l.qty).map(l => ({ product_id: Number(l.product_id), qty: Number(l.qty) }));
    if (!items.length) { showToast('Add at least one product', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.post('/stock-transfers', { ...form, from_branch_id: Number(form.from_branch_id), to_branch_id: Number(form.to_branch_id), from_location_id: Number(form.from_location_id), to_location_id: Number(form.to_location_id), items });
      showToast(res.data.message);
      setShowForm(false); setForm(emptyForm); setLines([{ product_id:'', qty:'' }]); load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const grandTotal = lines.reduce((s, l) => {
    const stock = fromStock.find(i => String(i.product_id) === String(l.product_id));
    return s + (Number(l.qty) * Number(stock?.cost_price || 0));
  }, 0);

  // --- Receive modal handlers ---
  const openReceive = (t: any) => {
    setReceiveTarget(t);
    setReceiveDate(new Date().toISOString().split('T')[0]);
    setReceiveLanded([]);
  };
  const closeReceive = () => setReceiveTarget(null);
  const addReceiveLanded    = () => setReceiveLanded(l => [...l, { cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value', description:'' }]);
  const removeReceiveLanded = (i: number) => setReceiveLanded(l => l.filter((_,idx) => idx !== i));
  const updateReceiveLanded = (i: number, f: string, v: string) => setReceiveLanded(l => l.map((lc,idx) => idx===i ? {...lc,[f]:v} : lc));

  const submitReceive = async () => {
    if (!receiveTarget) return;
    const lc = receiveLanded.filter(l => l.amount && l.credit_account_id).map(l => ({ cost_type: l.cost_type, amount: Number(l.amount), credit_account_id: Number(l.credit_account_id), allocation_method: l.allocation_method, description: l.description }));
    setReceiving(true);
    try {
      const res = await api.post('/stock-transfers/' + receiveTarget.id + '/receive', { received_date: receiveDate, landed_costs: lc });
      showToast(res.data.message);
      closeReceive();
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed to receive', 'error'); }
    finally { setReceiving(false); }
  };

  return (
    <div>
      <div className='page-header'>
        <h1 className='page-title'>Stock Transfer</h1>
        <button className='btn btn-primary' type='button' onClick={() => setShowForm(!showForm)}><ArrowRightLeft size={13} /> New Transfer</button>
      </div>
      {showForm && (
        <div className='card' style={{ marginBottom:16 }}>
          <div className='card-header'><span>New Stock Transfer (Dispatch)</span><button className='btn btn-secondary btn-sm' type='button' onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className='card-body'>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
              <div className='form-group'>
                <label className='form-label'>From Branch *</label>
                <select className='form-control' value={form.from_branch_id} onChange={e => setForm((f:any) => ({...f, from_branch_id:e.target.value, from_location_id:''}))}>
                  <option value=''>Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>From Location *</label>
                <select className='form-control' value={form.from_location_id} onChange={e => setForm((f:any) => ({...f, from_location_id:e.target.value}))}>
                  <option value=''>Select location</option>
                  {fromLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>Transfer Date *</label>
                <input type='date' className='form-control' value={form.transfer_date} onChange={e => setForm((f:any) => ({...f, transfer_date:e.target.value}))} />
              </div>
              <div className='form-group'>
                <label className='form-label'>To Branch *</label>
                <select className='form-control' value={form.to_branch_id} onChange={e => setForm((f:any) => ({...f, to_branch_id:e.target.value, to_location_id:''}))}>
                  <option value=''>Select branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>To Location *</label>
                <select className='form-control' value={form.to_location_id} onChange={e => setForm((f:any) => ({...f, to_location_id:e.target.value}))}>
                  <option value=''>Select location</option>
                  {toLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className='form-group'>
                <label className='form-label'>Notes</label>
                <input className='form-control' value={form.notes} onChange={e => setForm((f:any) => ({...f, notes:e.target.value}))} />
              </div>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Available</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Unit Cost</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
                <th style={{ padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                <th></th>
              </tr></thead>
              <tbody>
                {lines.map((line, i) => {
                  const stock = fromStock.find(s => String(s.product_id) === String(line.product_id));
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'6px 4px' }}>
                        <LocalSearchSelect
                          options={fromStock.map((s: any) => ({ value: s.product_id, label: s.product_name, sublabel: s.sku }))}
                          value={line.product_id}
                          fontSize={12}
                          placeholder='Search product...'
                          onSelect={(v) => updateLine(i, 'product_id', String(v))}
                        />
                      </td>
                      <td style={{ padding:'6px 4px', fontSize:12, color:'var(--green)' }}>{stock ? Number(stock.qty_available).toLocaleString() : '-'}</td>
                      <td style={{ padding:'6px 4px', fontSize:12 }}>{stock ? Number(stock.cost_price).toFixed(2) : '-'}</td>
                      <td style={{ padding:'6px 4px' }}><input type='number' className='form-control' value={line.qty} onChange={e => updateLine(i,'qty',e.target.value)} style={{ fontSize:12, textAlign:'right', width:80 }} /></td>
                      <td style={{ padding:'6px 4px', fontSize:12, textAlign:'right' }}>{stock && line.qty ? (Number(line.qty) * Number(stock.cost_price)).toFixed(2) : '0.00'}</td>
                      <td style={{ padding:'6px 4px' }}><button className='btn btn-secondary btn-sm' type='button' onClick={() => removeLine(i)}>x</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button className='btn btn-secondary btn-sm' type='button' onClick={addLine} style={{ marginBottom:16 }}>+ Add Product</button>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:14, fontWeight:700, color:'var(--red)' }}>Transfer Total: {grandTotal.toFixed(2)}</span>
              <button className='btn btn-primary' type='button' onClick={handleSubmit} disabled={saving}>{saving ? 'Dispatching...' : 'Dispatch Transfer'}</button>
            </div>
          </div>
        </div>
      )}

      {receiveTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className='card' style={{ width:520, maxHeight:'85vh', overflowY:'auto' }}>
            <div className='card-header'><span>Receive Transfer {receiveTarget.transfer_no}</span><button className='btn btn-secondary btn-sm' type='button' onClick={closeReceive}>Cancel</button></div>
            <div className='card-body'>
              <div className='form-group'>
                <label className='form-label'>Received Date *</label>
                <input type='date' className='form-control' value={receiveDate} onChange={e => setReceiveDate(e.target.value)} />
              </div>
              <div style={{ margin:'16px 0' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--tx2)' }}>Landed Costs (Optional)</span>
                  <button className='btn btn-secondary btn-sm' type='button' onClick={addReceiveLanded}>+ Add Cost</button>
                </div>
                {receiveLanded.map((lc, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr auto', gap:8, marginBottom:8, alignItems:'end' }}>
                    <div className='form-group' style={{ marginBottom:0 }}>
                      <label className='form-label'>Type</label>
                      <select className='form-control' value={lc.cost_type} onChange={e => updateReceiveLanded(i,'cost_type',e.target.value)}>
                        <option>Transport</option><option>Customs</option><option>Insurance</option><option>Other</option>
                      </select>
                    </div>
                    <div className='form-group' style={{ marginBottom:0 }}>
                      <label className='form-label'>Amount</label>
                      <input type='number' className='form-control' value={lc.amount} onChange={e => updateReceiveLanded(i,'amount',e.target.value)} />
                    </div>
                    <div className='form-group' style={{ marginBottom:0 }}>
                      <label className='form-label'>Credit Account</label>
                      <select className='form-control' value={lc.credit_account_id} onChange={e => updateReceiveLanded(i,'credit_account_id',e.target.value)}>
                        <option value=''>Select</option>
                        {coa.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                      </select>
                    </div>
                    <div className='form-group' style={{ marginBottom:0 }}>
                      <label className='form-label'>Allocation</label>
                      <select className='form-control' value={lc.allocation_method} onChange={e => updateReceiveLanded(i,'allocation_method',e.target.value)}>
                        <option value='by_value'>By Value</option><option value='by_qty'>By Qty</option>
                      </select>
                    </div>
                    <button className='btn btn-secondary btn-sm' type='button' onClick={() => removeReceiveLanded(i)}>x</button>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <button className='btn btn-primary' type='button' onClick={submitReceive} disabled={receiving}>{receiving ? 'Receiving...' : 'Confirm Receive'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='card'>
        <div className='card-body' style={{ padding:0 }}>
          {loading ? <div style={{ padding:20, textAlign:'center' }}>Loading...</div> : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Transfer No</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>From</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>To</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Date</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Status</th>
                <th style={{ padding:'10px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Landed Cost</th>
                <th></th>
              </tr></thead>
              <tbody>
                {transfers.map((t:any) => {
                  const status = statusOf(t);
                  return (
                    <tr key={t.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                      <td style={{ padding:'10px 12px', fontSize:12, fontWeight:600, color:'var(--blue)' }}>{t.transfer_no}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.from_branch} - {t.from_location}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.to_branch} - {t.to_location}</td>
                      <td style={{ padding:'10px 12px', fontSize:12 }}>{t.transfer_date}</td>
                      <td style={{ padding:'10px 12px' }}><StatusBadge status={status} /></td>
                      <td style={{ padding:'10px 12px', fontSize:12, textAlign:'right' }}>{Number(t.landed_cost_total || 0).toFixed(2)}</td>
                      <td style={{ padding:'10px 12px', textAlign:'right' }}>
                        {status === 'in_transit' && (
                          <button className='btn btn-primary btn-sm' type='button' onClick={() => openReceive(t)}>
                            <PackageCheck size={12} /> Receive
                          </button>
                        )}
                        {status === 'draft' && <Truck size={14} color='var(--tx3)' />}
                      </td>
                    </tr>
                  );
                })}
                {!transfers.length && <tr><td colSpan={7} style={{ padding:20, textAlign:'center', color:'var(--tx3)' }}>No transfers yet</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
